import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useTour } from "@/contexts/TourContext";
import {
  LayoutDashboard, FolderKanban, ListChecks, FileText, Bell, Users,
  ArrowRight, LogIn, CheckCircle2, PlayCircle,
} from "lucide-react";

const BASE_STEPS = [
  {
    icon: LogIn,
    title: "1. Faça login",
    text: "Acesse o portal com o e-mail e a senha enviados pela equipe BAUEN. No primeiro acesso, você recebe um link por e-mail para criar sua própria senha.",
  },
  {
    icon: LayoutDashboard,
    title: "2. Conheça seu painel",
    text: "No 'Meu Painel' você vê um resumo: projetos ativos, documentos enviados, pendências e o progresso geral do cronograma da sua obra.",
  },
  {
    icon: FolderKanban,
    title: "3. Acompanhe seus projetos",
    text: "Em 'Meus Projetos' estão todas as obras vinculadas à sua empresa. Clique em um projeto para ver detalhes, prazo e responsável.",
  },
  {
    icon: ListChecks,
    title: "4. Veja o Cronograma",
    text: "Dentro de um projeto, a aba 'Cronograma' mostra o checklist de etapas da obra (com status e data prevista). Você acompanha o percentual concluído, mas apenas a equipe BAUEN pode alterar as etapas.",
  },
  {
    icon: FileText,
    title: "5. Documentos",
    text: "Na aba 'Documentos', organizados por categoria, você pode visualizar, baixar, comentar e enviar novos arquivos quando solicitado. Cada documento tem um status (ex: Recebido, Em análise, Aprovado) e um histórico completo.",
  },
  {
    icon: Bell,
    title: "6. Notificações",
    text: "Sempre que um documento for atualizado, uma etapa concluída ou uma mensagem enviada, você recebe uma notificação — visível pelo sininho no menu lateral.",
  },
];

const OWNER_STEP = {
  icon: Users,
  title: "7. Gerencie sua equipe (perfil Dono)",
  text: "Como Dono da conta, você acessa a aba 'Usuários' para cadastrar colegas da sua empresa, definir o perfil de cada um (Dono ou Gestor), redefinir senhas e ativar/desativar acessos.",
};

function Step({ icon: Icon, title, text, testId }) {
  return (
    <Card className="card-lift" data-testid={testId}>
      <CardContent className="p-6 flex gap-4">
        <div className="p-2.5 rounded-xl bg-primary/10 text-primary shrink-0 h-fit">
          <Icon size={18} />
        </div>
        <div>
          <h3 className="font-heading font-semibold">{title}</h3>
          <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{text}</p>
        </div>
      </CardContent>
    </Card>
  );
}

export default function ClientGuidePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { start } = useTour();
  const steps = user?.client_role === "DONO" ? [...BASE_STEPS, OWNER_STEP] : BASE_STEPS;

  return (
    <div className="space-y-8" data-testid="client-guide-page">
      <header className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground font-medium">Primeiros passos</p>
          <h1 className="font-heading text-3xl font-bold mt-1">Guia de uso do Portal BAUEN</h1>
          <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
            Um passo a passo rápido para você entender como acompanhar seus projetos, documentos e cronograma dentro do portal.
          </p>
        </div>
        <Button variant="outline" onClick={() => start()} className="gap-2 shrink-0" data-testid="restart-tour-btn">
          <PlayCircle size={15} /> Rever tutorial interativo
        </Button>
      </header>

      <div className="grid sm:grid-cols-2 gap-4">
        {steps.map((s, i) => <Step key={i} {...s} testId={`guide-step-${i + 1}`} />)}
      </div>

      <Card className="bg-primary/5 border-primary/20">
        <CardContent className="p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="text-primary shrink-0" size={22} />
            <div>
              <p className="font-heading font-semibold">Pronto para começar?</p>
              <p className="text-sm text-muted-foreground">Vá para o seu painel e acompanhe seus projetos em tempo real.</p>
            </div>
          </div>
          <Button onClick={() => navigate("/")} className="gap-2 shrink-0" data-testid="guide-go-to-dashboard-btn">
            Ir para o painel <ArrowRight size={15} />
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
